import { FunctionComponent, useContext, useEffect, useState } from "react";
import cx from "classnames";

import { getAvailableDice } from "./store/dice";
import {
  applyMoveToGameBoardState,
  didPlayerWin,
  setGameBoardState,
} from "./store/gameBoardSlice";
import { GameState } from "./store/gameStateSlice";
import {
  clearLastPointClicked,
  setLastPointClicked,
} from "./store/lastPointClickedSlice";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import {
  areProvisionalMovesSubmittable,
  doesPlayerOccupyBar,
  getAllPossibleMoveSets,
  getInverseMove,
  LongNardyContext,
} from "./store/moves";
import { getHighlightedMoveSetsForSelectedPoint } from "./store/moveHighlights";
import { appendProvisionalMove } from "./store/provisionalMovesSlice";
import { setShowGameOverDialog } from "./store/settingsSlice";
import { Color, GameMode, GameResult, Move, MovementDirection, Player } from "./Types";
import Bar from "./Bar";
import BoardPoint, { BoardPointLocation } from "./BoardPoint";
import Dice from "./Dice";
import Home from "./Home";
import { Animation, createAnimationData } from "./Animations";
import BeginGameButton from "./BeginGameButton";
import OpeningDiceRoll from "./OpeningDiceRoll";
import { isCurrentPlayer, isGameOverState } from "./Utils";
import { ActionsContext, LocalGameActions } from "./ActionsContext";
import {
  dequeueAnimatableMove,
  enqueueAnimatableMoves,
} from "./store/animatableMovesSlice";
import RollButton from "./RollButton";
import OfferDoubleButton from "./OfferDoubleButton";
import { CHECKER_ANIMATION_PULSE_TIMER_MS } from "./Constants";
import { MatchScore } from "./store/matchScoreSlice";
import { store } from "./store/store";


enum BoardQuadrants {
  TopLeft = "TOP_LEFT",
  TopRight = "TOP_RIGHT",
  BottomLeft = "BOTTOM_LEFT",
  BottomRight = "BOTTOM_RIGHT",
}

const BOARD_POINT_LAYOUTS = {
  [Player.One]: {
    [MovementDirection.CounterClockwise]: {
      [BoardQuadrants.TopLeft]: {
        start: 12,
        end: 17,
        increment: 1,
      },
      [BoardQuadrants.TopRight]: {
        start: 18,
        end: 23,
        increment: 1,
      },
      [BoardQuadrants.BottomLeft]: {
        start: 11,
        end: 6,
        increment: -1,
      },
      [BoardQuadrants.BottomRight]: {
        start: 5,
        end: 0,
        increment: -1,
      },
    },
    [MovementDirection.Clockwise]: {
      [BoardQuadrants.TopLeft]: {
        start: 23,
        end: 18,
        increment: -1,
      },
      [BoardQuadrants.TopRight]: {
        start: 17,
        end: 12,
        increment: -1,
      },
      [BoardQuadrants.BottomLeft]: {
        start: 0,
        end: 5,
        increment: 1,
      },
      [BoardQuadrants.BottomRight]: {
        start: 6,
        end: 11,
        increment: 1,
      },
    },
  },
  [Player.Two]: {
    // CounterClockwise: P2 видит свою доску зеркально — дальние 2 шашки (pt 0) справа сверху,
    // домашняя четверть (18-23) справа снизу — как у P1 в своей раскладке.
    [MovementDirection.CounterClockwise]: {
      [BoardQuadrants.TopLeft]: {
        start: 11,
        end: 6,
        increment: -1,
      },
      [BoardQuadrants.TopRight]: {
        start: 5,
        end: 0,
        increment: -1,
      },
      [BoardQuadrants.BottomLeft]: {
        start: 12,
        end: 17,
        increment: 1,
      },
      [BoardQuadrants.BottomRight]: {
        start: 18,
        end: 23,
        increment: 1,
      },
    },
    [MovementDirection.Clockwise]: {
      [BoardQuadrants.TopLeft]: {
        start: 0,
        end: 5,
        increment: 1,
      },
      [BoardQuadrants.TopRight]: {
        start: 6,
        end: 11,
        increment: 1,
      },
      [BoardQuadrants.BottomLeft]: {
        start: 23,
        end: 18,
        increment: -1,
      },
      [BoardQuadrants.BottomRight]: {
        start: 17,
        end: 12,
        increment: -1,
      },
    },
  },
};

// Длинные нарды — своя раскладка доски:
// P1: голова (точка 0) в верхнем правом углу, идёт против ч/с → HOME справа
// P2: зеркало (поворот 180°) — голова (точка 12) в верхнем правом углу
const LONG_NARDY_LAYOUT_P1 = {
  [BoardQuadrants.TopRight]: {
    start: 5,
    end: 0,
    increment: -1,    // 5,4,3,2,1,0 → P1 голова (0) справа
  },
  [BoardQuadrants.TopLeft]: {
    start: 11,
    end: 6,
    increment: -1,
  },
  [BoardQuadrants.BottomLeft]: {
    start: 12,
    end: 17,
    increment: 1,     // P2 голова (12) слева снизу
  },
  [BoardQuadrants.BottomRight]: {
    start: 18,
    end: 23,
    increment: 1,
  },
};

// P2-зеркало: поворот 180° относительно P1-раскладки
// P2 голова (12) — правый верхний угол, P2 HOME — справа
const LONG_NARDY_LAYOUT_P2 = {
  [BoardQuadrants.TopRight]: {
    start: 17,
    end: 12,
    increment: -1,    // 17,16,15,14,13,12 → P2 голова (12) справа
  },
  [BoardQuadrants.TopLeft]: {
    start: 23,
    end: 18,
    increment: -1,
  },
  [BoardQuadrants.BottomLeft]: {
    start: 0,
    end: 5,
    increment: 1,     // P1 голова (0) слева снизу
  },
  [BoardQuadrants.BottomRight]: {
    start: 6,
    end: 11,
    increment: 1,     // P2 домашняя четверть (6-11) у HOME
  },
};

type GameBoardProps = {
  playerPerspective: Player;
};

const GameBoard: FunctionComponent<GameBoardProps> = ({
  playerPerspective,
}: GameBoardProps) => {
  const [
    currentPlayer,
    settings,
    diceData,
    originalGameBoardState,
    gameState,
    lastPointClicked,
    provisionalMoves,
    players,
    animatableMoves,
    matchScore,
    doublingCubeData,
    readyForNextGameData,
  ] = useAppSelector((state) => [
    state.currentPlayer,
    state.settings,
    state.dice,
    state.gameBoard,
    state.gameState,
    state.lastPointClicked,
    state.provisionalMoves,
    state.players,
    state.animatableMoves,
    state.matchScore,
    state.doublingCube,
    state.readyForNextGame,
  ]);
  const gameMode = settings.gameMode;
  const actions = useContext(ActionsContext);

  const playerOneColor = settings.playerOneColor;
  const playerTwoColor =
    settings.playerOneColor === Color.White ? Color.Black : Color.White;
  // В длинных нардах движение всегда против часовой стрелки
  const playerMovementDirection =
    gameMode === GameMode.LongNardy
      ? MovementDirection.CounterClockwise
      : settings.movementDirection;

  const dispatch = useAppDispatch();
  const [disableSubmitButton, setDisableSubmitButton] = useState(false);
  const [disableHighlightedMoves, setDisableHighlightedMoves] = useState(false);
  const [currAnimations, setCurrAnimations] = useState(new Array<Animation>());

  const currAnimatableMove =
    animatableMoves.length > 0 ? animatableMoves[0] : null;

  // If we have a current animating move, apply it on top of the base board state.
  const gameBoardState =
    currAnimatableMove != null
      ? applyMoveToGameBoardState(
          originalGameBoardState,
          currAnimatableMove.move
        )
      : originalGameBoardState;

  const onAnimationComplete = (id: number) => {
    // If this animation id is not present, then a sibling animation must've already
    // finished before it. Just do nothing.
    if (!currAnimations.some((a) => a.id === id)) {
      return;
    }

    if (currAnimatableMove == null) {
      console.error("Current animatable move is unexpectedly null.");
      return;
    }

    // ВАЖНО: пересчитываем новый board напрямую из двух СВЕЖИХ значений
    // (originalGameBoardState из Redux + move из очереди анимаций).
    // Раньше использовалось замыкание `gameBoardState`, что могло дать
    // устаревшее значение и создавать "призрачные" шашки не там где надо.
    const newBoard = applyMoveToGameBoardState(
      originalGameBoardState,
      currAnimatableMove.move
    );

    dispatch(setGameBoardState(newBoard));
    dispatch(dequeueAnimatableMove());
    setCurrAnimations([]);

    // Check to see if the move that just finished animating causes the game to end.
    const gameResult = didPlayerWin(newBoard, currentPlayer, gameMode);
    if (gameResult === GameResult.NotOver) {
      return;
    } else {
      // If this is the client of the winning player, call the gameOver action which will
      // write the changes to the DB and display the gameOver dialog locally for the winning
      // player.
      // В локальной игре (с ботом) результат обрабатывает "главный" клиент всегда.
      // В сетевой — только победитель пишет результат в Firestore.
      const isLocalGame = actions instanceof LocalGameActions;
      const shouldPushResult =
        isLocalGame || isCurrentPlayer(players, currentPlayer, actions);

      if (shouldPushResult) {
        let winningGameState = GameState.GameOver;
        let multiplier = 1;
        if (gameResult === GameResult.PlayerWonGammon) {
          winningGameState = GameState.GameOverGammon;
          multiplier = 2;
        } else if (gameResult === GameResult.PlayerWonBackgammon) {
          winningGameState = GameState.GameOverBackgammon;
          multiplier = 3;
        }

        const pointsWon =
          (doublingCubeData.enabled ? doublingCubeData.gameStakes : 1) *
          multiplier;

        const newMatchScore: MatchScore = {
          [Player.One]:
            matchScore[Player.One] +
            (currentPlayer === Player.One ? pointsWon : 0),
          [Player.Two]:
            matchScore[Player.Two] +
            (currentPlayer === Player.Two ? pointsWon : 0),
          pointsRequiredToWin: matchScore.pointsRequiredToWin,
        };

        actions.gameOver(
          newBoard,
          winningGameState,
          newMatchScore,
          currentPlayer === Player.One ? Player.Two : Player.One,
          provisionalMoves
        );
      } else {
        // Сетевой клиент проигравшего — просто показываем диалог,
        // победитель запишет результат в Firestore.
        dispatch(setShowGameOverDialog(true));
      }
    }
  };

  // Запускаем подготовку анимации в эффекте, а не в теле render.
  // Раньше setCurrAnimations вызывался прямо во время рендера —
  // в StrictMode это давало двойные/нестабильные обновления и
  // совпадения с watchdog'ом. Зависит только от animationID,
  // т.е. на каждый новый ход создаётся ровно один раз.
  useEffect(() => {
    if (currAnimatableMove == null) return;
    if (currAnimations.length > 0) return;
    const data = createAnimationData(
      originalGameBoardState,
      currAnimatableMove,
      playerMovementDirection,
      playerPerspective,
      gameMode
    );
    setCurrAnimations(data);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currAnimatableMove?.animationID]);

  const availableDice = getAvailableDice(
    diceData.currentRoll,
    provisionalMoves
  );

  // Watchdog: страховка от потерянного onTransitionEnd. Если конкретно ЭТА
  // анимация (по её animationID) висит дольше 2.5с — принудительно завершаем её
  // и, ЕСЛИ ход оказался победным, показываем диалог завершения игры.
  useEffect(() => {
    if (currAnimatableMove == null) return;
    const lockedID = currAnimatableMove.animationID;
    const lockedMove = currAnimatableMove.move;
    const timer = setTimeout(() => {
      const cur = store.getState();
      const head = cur.animatableMoves[0];
      if (head == null || head.animationID !== lockedID) return;

      const newBoard = applyMoveToGameBoardState(cur.gameBoard, lockedMove);
      dispatch(setGameBoardState(newBoard));
      dispatch(dequeueAnimatableMove());
      setCurrAnimations([]);

      // Проверяем победу: если ход был последним в победной позиции —
      // показываем диалог (как в onAnimationComplete).
      const gameResult = didPlayerWin(newBoard, cur.currentPlayer, cur.settings.gameMode);
      if (gameResult !== GameResult.NotOver) {
        dispatch(setShowGameOverDialog(true));
      }
    }, 2500);
    return () => clearTimeout(timer);
  }, [currAnimatableMove?.animationID]); // eslint-disable-line

  // Setup listener to display warning dialog if user tries to navigate away.
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Trigger confirmation dialog before navigating away.
      event.preventDefault();
      // Included for legacy support
      event.returnValue = true;
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Setup checker pulse animation timer
  const [checkerPulse, setCheckerPulse] = useState(false);
  useEffect(() => {
    const interval = setInterval(() => {
      setCheckerPulse(!checkerPulse);
    }, CHECKER_ANIMATION_PULSE_TIMER_MS);
    return () => clearInterval(interval);
  }, [checkerPulse]);

  // Check if game is over and both players are ready for next game
  useEffect(() => {
    if (
      actions.isHostClient() &&
      isGameOverState(gameState) &&
      readyForNextGameData[Player.One] &&
      readyForNextGameData[Player.Two]
    ) {
      actions.bothPlayersReadyForNextGame();
    }
  }, [actions, gameState, readyForNextGameData]);

  const isPlayerActivelyMoving =
    gameState === GameState.PlayerMoving &&
    isCurrentPlayer(players, currentPlayer, actions);

  let allPossibleInitialMoves: Move[] = [];
  let allPossibleMoveSetsFromSelectedPoint: Move[][] = [];

  const longNardyCtx: LongNardyContext | undefined =
    gameMode === GameMode.LongNardy
      ? { provisionalMoves }
      : undefined;

  if (
    isPlayerActivelyMoving &&
    currAnimatableMove === null &&
    !disableHighlightedMoves
  ) {
    // Get all move sets and filter to max-length only (must use max dice / bigger die rule).
    // Only compute if there are dice remaining to play.
    if (availableDice.length > 0) {
      const allOptimalMoveSets = getAllPossibleMoveSets(
        gameBoardState,
        availableDice,
        currentPlayer,
        gameMode,
        longNardyCtx
      );
      const maxMoveCount = allOptimalMoveSets.reduce(
        (max, ms) => Math.max(max, ms.length),
        0
      );

      // maxMoveCount === 0 means all moves are blocked — show no highlights
      if (maxMoveCount > 0) {
        let maxMoveSets = allOptimalMoveSets.filter(
          (ms) => ms.length === maxMoveCount
        );

        // Правило бóльшего кубика: если можно сыграть только 1 из 2 костей,
        // обязательно играть бóльшую. Фильтруем подсветку под это правило.
        if (maxMoveCount === 1 && availableDice.length >= 2) {
          const maxDieUsable = maxMoveSets.reduce(
            (max, ms) => Math.max(max, ms[0].dieUsed),
            0
          );
          const biggerDieSets = maxMoveSets.filter(
            (ms) => ms[0].dieUsed === maxDieUsable
          );
          if (biggerDieSets.length > 0) {
            maxMoveSets = biggerDieSets;
          }
        }

        if (lastPointClicked.point === -1) {
          // Подсвечиваем только шашки — начало оптимального хода
          const seenSrc = new Set<Move["from"]>();
          allPossibleInitialMoves = maxMoveSets
            .map((ms) => ms[0])
            .filter((m) => {
              if (seenSrc.has(m.from)) return false;
              seenSrc.add(m.from);
              return true;
            });
        } else if (lastPointClicked.point !== -1) {
          const selected = lastPointClicked.point;
          allPossibleMoveSetsFromSelectedPoint =
            getHighlightedMoveSetsForSelectedPoint(maxMoveSets, selected);
        }
      }
    }
  }

  const boardPointClickHandler = (
    pointClicked: number | "BAR" | "HOME"
  ): boolean => {
    // Disable click handler if it's not a player's turn or if the client is not
    // the current player.
    if (!isPlayerActivelyMoving) {
      return true;
    }

    // Guard #5: в коротких нардах при наличии шашки на баре — только ход с бара
    if (gameMode !== GameMode.LongNardy &&
        doesPlayerOccupyBar(gameBoardState, currentPlayer) &&
        pointClicked !== "BAR" &&
        lastPointClicked.point !== "BAR") {
      dispatch(clearLastPointClicked());
      return false;
    }

    // Ищем набор из выбранной точки, финальный пункт которого = кликнутая точка.
    // Набор может быть одно- или многошаговым (сумма кубиков) — применяем все ходы.
    const moveSetsToApply = allPossibleMoveSetsFromSelectedPoint.filter(
      (moveSet: Move[]) =>
        moveSet[0].from === lastPointClicked.point &&
        moveSet[moveSet.length - 1].to === pointClicked
    );
    if (moveSetsToApply.length > 0) {
      // Если несколько вариантов — берём наиболее длинный (максимум кубиков)
      let bestSet = moveSetsToApply[0];
      for (let i = 1; i < moveSetsToApply.length; i++) {
        if (moveSetsToApply[i].length > bestSet.length) {
          bestSet = moveSetsToApply[i];
        } else if (
          moveSetsToApply[i].length === bestSet.length &&
          moveSetsToApply[i][0].dieUsed > bestSet[0].dieUsed
        ) {
          bestSet = moveSetsToApply[i];
        }
      }
      // Применяем все ходы набора по очереди (одна или несколько кости)
      bestSet.forEach((move) => dispatch(appendProvisionalMove(move)));
      dispatch(enqueueAnimatableMoves(bestSet));
      dispatch(clearLastPointClicked());
      return true;
    } else if (pointClicked !== "HOME") {
      let hasMovesFromPoint = allPossibleInitialMoves.some(
        (move) => move.from === pointClicked
      );
      if (hasMovesFromPoint) {
        dispatch(setLastPointClicked({ point: pointClicked }));
      } else {
        dispatch(clearLastPointClicked());
        // Don't show red no-move highlight if player is just unselected last clicked point.
        if (pointClicked === lastPointClicked.point) {
          return true;
        }
      }
      return hasMovesFromPoint;
    }
    return false;
  };

  const submitButtonHandler = async () => {
    // Temporarily prevent the submit button from showing up to avoid it
    // appearing immediately when dice are still rolling when there are no
    // legal moves.
    setDisableSubmitButton(true);
    setTimeout(() => {
      setDisableSubmitButton(false);
    }, 1300);
    await actions.submitMoves(
      gameBoardState,
      currentPlayer === Player.One ? Player.Two : Player.One,
      provisionalMoves
    );
  };

  const topLeftPoints: Array<React.ReactNode> = [];
  const bottomLeftPoints: Array<React.ReactNode> = [];
  const topRightPoints: Array<React.ReactNode> = [];
  const bottomRightPoints: Array<React.ReactNode> = [];

  const pointsState = gameBoardState.pointsState;
  // Раскладка доски: зеркало для P2 в длинных нардах (поворот 180°)
  let layoutData =
    gameMode === GameMode.LongNardy
      ? (playerPerspective === Player.Two ? LONG_NARDY_LAYOUT_P2 : LONG_NARDY_LAYOUT_P1)
      : BOARD_POINT_LAYOUTS[playerPerspective][playerMovementDirection];

  let topLeftData = layoutData[BoardQuadrants.TopLeft];
  for (
    let i = topLeftData.start;
    i !== topLeftData.end + topLeftData.increment;
    i += topLeftData.increment
  ) {
    topLeftPoints.push(
      <BoardPoint
        key={i}
        pointState={pointsState[i]}
        clickHandler={boardPointClickHandler}
        allPossibleInitialMoves={allPossibleInitialMoves}
        allPossibleMoveSetsFromSelectedPoint={
          allPossibleMoveSetsFromSelectedPoint
        }
        lastPointClicked={lastPointClicked}
        location={BoardPointLocation.Top}
        playerOneColor={playerOneColor}
        playerTwoColor={playerTwoColor}
        pointNumber={i}
        currAnimations={currAnimations}
        onAnimationComplete={onAnimationComplete}
        checkerPulse={checkerPulse}
      />
    );
  }

  let topRightData = layoutData[BoardQuadrants.TopRight];
  for (
    let i = topRightData.start;
    i !== topRightData.end + topRightData.increment;
    i += topRightData.increment
  ) {
    topRightPoints.push(
      <BoardPoint
        key={i}
        pointState={pointsState[i]}
        clickHandler={boardPointClickHandler}
        allPossibleInitialMoves={allPossibleInitialMoves}
        allPossibleMoveSetsFromSelectedPoint={
          allPossibleMoveSetsFromSelectedPoint
        }
        lastPointClicked={lastPointClicked}
        location={BoardPointLocation.Top}
        playerOneColor={playerOneColor}
        playerTwoColor={playerTwoColor}
        pointNumber={i}
        currAnimations={currAnimations}
        onAnimationComplete={onAnimationComplete}
        checkerPulse={checkerPulse}
      />
    );
  }

  let bottomLeftData = layoutData[BoardQuadrants.BottomLeft];
  for (
    let i = bottomLeftData.start;
    i !== bottomLeftData.end + bottomLeftData.increment;
    i += bottomLeftData.increment
  ) {
    bottomLeftPoints.push(
      <BoardPoint
        key={i}
        pointState={pointsState[i]}
        clickHandler={boardPointClickHandler}
        allPossibleInitialMoves={allPossibleInitialMoves}
        allPossibleMoveSetsFromSelectedPoint={
          allPossibleMoveSetsFromSelectedPoint
        }
        lastPointClicked={lastPointClicked}
        location={BoardPointLocation.Bottom}
        playerOneColor={playerOneColor}
        playerTwoColor={playerTwoColor}
        pointNumber={i}
        currAnimations={currAnimations}
        onAnimationComplete={onAnimationComplete}
        checkerPulse={checkerPulse}
      />
    );
  }

  let bottomRightData = layoutData[BoardQuadrants.BottomRight];
  for (
    let i = bottomRightData.start;
    i !== bottomRightData.end + bottomRightData.increment;
    i += bottomRightData.increment
  ) {
    bottomRightPoints.push(
      <BoardPoint
        key={i}
        pointState={pointsState[i]}
        clickHandler={boardPointClickHandler}
        allPossibleInitialMoves={allPossibleInitialMoves}
        allPossibleMoveSetsFromSelectedPoint={
          allPossibleMoveSetsFromSelectedPoint
        }
        lastPointClicked={lastPointClicked}
        location={BoardPointLocation.Bottom}
        playerOneColor={playerOneColor}
        playerTwoColor={playerTwoColor}
        pointNumber={i}
        currAnimations={currAnimations}
        onAnimationComplete={onAnimationComplete}
        checkerPulse={checkerPulse}
      />
    );
  }

  const home = (
    <Home
      homeState={gameBoardState.homeState}
      isHighlighted={allPossibleMoveSetsFromSelectedPoint.some(
        (moveSet: Move[]) => moveSet[moveSet.length - 1].to === "HOME"
      )}
      clickHandler={boardPointClickHandler}
      currentPlayer={currentPlayer}
      playerOneColor={playerOneColor}
      playerTwoColor={playerTwoColor}
      currAnimations={currAnimations}
      onAnimationComplete={onAnimationComplete}
      checkerPulse={checkerPulse}
      playerMovementDirection={playerMovementDirection}
      playerPerspective={playerPerspective}
    />
  );

  const onRollButtonClicked = () => {
    // Prevent highlights on possible moves from appearing
    // until after the dice have finished animating.
    setDisableHighlightedMoves(true);
    setTimeout(() => {
      setDisableHighlightedMoves(false);
    }, 1300);
  };

  let diceComponent = null;
  if (gameState === GameState.CoinFlip) {
    diceComponent = <OpeningDiceRoll />;
  } else if (gameState === GameState.PlayerMoving) {
    let canSubmitMoves = false;
    // Кнопка «галочка» появляется только когда полностью пусты обе очереди:
    // отрисовываемые сейчас анимации (currAnimations) и буфер ходов
    // (animatableMoves). Раньше проверка только по currAnimations иногда
    // ложно срабатывала между шагами многоходового перемещения.
    if (currAnimations.length === 0 && animatableMoves.length === 0) {
      // Unwind the provisional moves to reconstruct the game board state
      // before there were any provisional moves so that we can validate
      // that the provisional move set is legal to submit.
      let gameBoardStateBeforeProvisionalMoves = gameBoardState;
      for (let i = provisionalMoves.length - 1; i >= 0; i--) {
        gameBoardStateBeforeProvisionalMoves = applyMoveToGameBoardState(
          gameBoardStateBeforeProvisionalMoves,
          getInverseMove(provisionalMoves[i])
        );
      }
      // For submittability check we pass an empty Long Nardy context because
      // areProvisionalMovesSubmittable recalculates all possible move sets from
      // the original board state. If we pass the current provisional moves,
      // the head rule check blocks all head moves and maxNumberOfMoves becomes 0.
      const emptyLongNardyCtx: LongNardyContext | undefined =
        gameMode === GameMode.LongNardy
          ? { provisionalMoves: [] }
          : undefined;

      canSubmitMoves =
        !disableSubmitButton &&
        isPlayerActivelyMoving &&
        areProvisionalMovesSubmittable(
          gameBoardStateBeforeProvisionalMoves,
          diceData.currentRoll,
          currentPlayer,
          provisionalMoves,
          gameMode,
          emptyLongNardyCtx
        );
    }

    diceComponent = (
      <Dice
        currentPlayerColor={
          currentPlayer === Player.One ? playerOneColor : playerTwoColor
        }
        availableDice={availableDice}
        diceValues={diceData.currentRoll}
        canSubmit={canSubmitMoves}
        submitButtonHandler={submitButtonHandler}
      />
    );
  } else {
    diceComponent = null;
  }

  // Home справа когда движение CounterClockwise (теперь единообразно для обоих игроков)
  const isHomeOnRight =
    gameMode === GameMode.LongNardy
      ? true
      : playerMovementDirection === MovementDirection.CounterClockwise;

  const isCW = playerMovementDirection === MovementDirection.Clockwise;

  const isLongNardy = gameMode === GameMode.LongNardy;

  return (
    <div className={cx("Game-board-wrapper", { "long-nardy": isLongNardy })}>
      {!isHomeOnRight ? home : null}
      <div
        className={cx("Game-board-half left", { cw: isCW })}
      >
        <OfferDoubleButton />
        <div className="Game-board-quadrant top">{topLeftPoints}</div>
        <div className="Game-board-quadrant bottom">{bottomLeftPoints}</div>
      </div>
      {!isLongNardy && (
        <Bar
          barState={gameBoardState.barState}
          checkerPulse={checkerPulse}
          clickHandler={boardPointClickHandler}
          currentPlayer={currentPlayer}
          playerOneColor={playerOneColor}
          playerTwoColor={playerTwoColor}
          currAnimations={currAnimations}
          onAnimationComplete={onAnimationComplete}
          allPossibleMoves={allPossibleInitialMoves}
          lastPointClicked={lastPointClicked}
        />
      )}
      <div
        className={cx("Game-board-half right", { cw: isCW })}
      >
        <BeginGameButton />
        <RollButton onRollButtonClicked={onRollButtonClicked} />
        {diceComponent}
        <div className="Game-board-quadrant top">{topRightPoints}</div>
        <div className="Game-board-quadrant bottom">{bottomRightPoints}</div>
      </div>
      {isHomeOnRight ? home : null}
    </div>
  );
};

export default GameBoard;
